import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// The one popover menu behind DownloadMenu (versions) and DecisionMenu
// (approve / reject): a captioned card of items anchored under a trigger.
// Rendered in a portal with fixed positioning so a card's overflow:hidden
// cannot clip it; focus moves into it on open (to the checked item when
// there is one), arrow keys / Home / End walk the items, Escape and a click
// or focus outside close it. The host renders its own trigger through
// `renderTrigger({ ref, open, toggle, onKeyDown })`.
// `items` = [{ key, children, onSelect, role?, checked? }]; `role` defaults
// to "menuitem" ("menuitemradio" for a single-choice menu, with `checked`).
export default function PopoverMenu({ heading, width = 240, renderTrigger, items }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useLayoutEffect(() => {
        if (!open || !btnRef.current) return;
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)) });
        const menu = menuRef.current;
        (menu?.querySelector('[aria-checked="true"]') || menu?.querySelector('[role^="menuitem"]'))?.focus();
    }, [open, width]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const close = () => setOpen(false);
        const onKey = (e) => { if (e.key === 'Escape') { close(); btnRef.current?.focus(); } };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    // Escape is handled here as well as on document: a host may stop key
    // events from bubbling (the request-card header does, so Enter on the
    // trigger does not toggle the card), which would otherwise swallow it.
    const closeToTrigger = () => { setOpen(false); btnRef.current?.focus(); };
    const onMenuKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeToTrigger(); return; }
        const els = Array.from(menuRef.current?.querySelectorAll('[role^="menuitem"]') || []);
        if (!els.length) return;
        const i = els.indexOf(document.activeElement);
        let next = null;
        if (e.key === 'ArrowDown') next = els[(i + 1) % els.length];
        else if (e.key === 'ArrowUp') next = els[(i - 1 + els.length) % els.length];
        else if (e.key === 'Home') next = els[0];
        else if (e.key === 'End') next = els[els.length - 1];
        if (next) { e.preventDefault(); next.focus(); }
    };
    const toggle = () => setOpen((v) => !v);
    const onTriggerKeyDown = (e) => { if (e.key === 'Escape' && open) { e.preventDefault(); closeToTrigger(); } };

    return (
        <>
            {renderTrigger({ ref: btnRef, open, toggle, onKeyDown: onTriggerKeyDown })}
            {open && createPortal(
                <div ref={menuRef} className="popover-menu" role="menu" aria-label={heading}
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width }}
                    onKeyDown={onMenuKeyDown}
                    onBlur={(e) => {
                        // focus left the menu (Tab past the last item, or a click elsewhere)
                        const next = e.relatedTarget;
                        if (!next || (!menuRef.current?.contains(next) && !btnRef.current?.contains(next))) setOpen(false);
                    }}>
                    <div className="popover-menu__heading">{heading}</div>
                    {items.map((item) => (
                        <button type="button" key={item.key} role={item.role || 'menuitem'}
                            aria-checked={item.role === 'menuitemradio' ? !!item.checked : undefined}
                            className={`popover-menu__item${item.checked ? ' popover-menu__item--checked' : ''}`}
                            onClick={() => { setOpen(false); item.onSelect(); }}>
                            {item.children}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
}
